import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal, viewChild } from '@angular/core';
import { Pipe, PipeTransform } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { AugmentedItem, TimeTableData } from './chargeable-hours-page';
import { PoSelect } from './po-select';

@Pipe({ name: 'sumBy' })
export class SumByPipe implements PipeTransform {
  transform(items: AugmentedItem[] | void, column: 'NotInvoicedYet', initial = 0): number {
    if (!items) {
      return initial;
    }
    return items.reduce((accum, { [column]: next }) => accum + (+next || 0), initial);
  }
}

@Component({
  selector: 'lcm-chargeable-hours-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTableModule,
    MatIconModule,
    MatTooltipModule,
    MatSortModule,
    MatButtonModule,
    MatInputModule,
    FormsModule,
    PoSelect,
    DecimalPipe,
    SumByPipe,
    RouterModule,
  ],
  templateUrl: './chargeable-hours-table.html',
  styles: [
    `:host{overflow-x: auto;}`
  ]
})
export class ChargeableHoursTable {
  refreshed = output<AugmentedItem>();

  items = input.required<AugmentedItem[]>();
  timeTable = input.required<TimeTableData>();

  private sort = viewChild.required(MatSort);
  filterPo = signal('');
  filter = signal('');

  totalHours = computed(() => {
    const timeTable = this.timeTable();
    const items = this.items();
    let total = 0;
    if (!timeTable) return total;

    for (const key in timeTable) {
      const time = timeTable[key].time;
      // Skip negative hours and items that are on hold
      if (time < 0) continue;
      const item = items.find(({ JiraTitle }) => JiraTitle === key);
      if (!item || item.Status === 'On Hold') continue;
      total += time;
    }
    return total;
  });

  columnsToShow = signal([
    'ClientCode',
    'JobNumber',
    'Title',
    // 'Type',
    'ProductOwner',
    'Status',
    '_Timestamp',
    '_Time',
    'actions',
  ]);

  private extended = computed(() => {
    const timeTable = this.timeTable();
    return this.items().map((item) => {
      const rec = timeTable[item.JiraTitle];
      if (!rec) return item;

      return {
        ...item,
        _Loaded: true,
        _Timestamp: rec.timestamp,
        _Time: rec.time,
        _LastUpdated: rec.lastUpdated,
        _Holded: rec.isPotentiallyHolded,
      };
    });
  });

  filteredItems = computed(() => {
    let items = this.extended();
    const po = this.filterPo();
    if (po) {
      items = items.filter((it) => it.ProductOwner === po);
    }
    return items;
  });

  source = computed(() => {
    const source = new MatTableDataSource(this.filteredItems());
    source.sort = this.sort();
    source.filter = this.filter();
    return source;
  });

  refreshOne(item: AugmentedItem) {
    this.refreshed.emit(item);
  }

  trackBy(idx: number, row: AugmentedItem) {
    return row.JiraTitle;
  }
}
